// Live ticking for Free talk (Pro tiers only).
//
// While a Pro learner records in Free talk, a 16 kHz PCM copy of the SAME
// microphone stream goes to ElevenLabs Scribe v2 Realtime over a WebSocket,
// opened with a single-use token our backend mints (the API key never reaches
// the browser). Each partial transcript is checked for the learner's practice
// words. The transcript itself is never kept, shown or sent anywhere: only which
// words ticked. Any failure (no token, socket error, no AudioWorklet) ends the
// session quietly and the step falls back to tap ticking.

export interface LiveWord {
  word: string
  /** Other written forms that count, e.g. the traditional form. */
  forms?: string[]
}

export type TranscriptKind = 'partial' | 'committed'

export interface ParsedMessage {
  kind: TranscriptKind | 'error' | 'other'
  text: string
}

export interface HeardState {
  /** Words present in the previous partial of the current segment. */
  previous: string[]
  /** Words already ticked. A tick never goes away. */
  heard: string[]
}

export const createHeardState = (): HeardState => ({ previous: [], heard: [] })

/** Strip whitespace and punctuation so "公 园" or "公园，" still match. */
export const normalizeTranscript = (text: string): string => (text || '').replace(/[\s\p{P}\p{S}]/gu, '')

/** Canonical practice words whose simplified or other form appears in the text. */
export const wordsInText = (text: string, words: LiveWord[]): string[] => {
  const clean = normalizeTranscript(text)
  if (!clean) return []
  return words
    .filter((w) =>
      [w.word, ...(w.forms || [])].some((form) => !!form && clean.indexOf(normalizeTranscript(form)) !== -1),
    )
    .map((w) => w.word)
}

/**
 * Feed one transcript into the tick state.
 *
 * A word ticks the first time it appears in any partial or committed
 * transcript. Partials arrive about once a second (measured on
 * scribe_v2_realtime with zh audio, 2026-09-29), so waiting for a second
 * sighting would add a full second of lag for little gain: the keyterms bias
 * the model towards these words either way. Ticked words never untick.
 */
export const reduceTranscript = (
  state: HeardState,
  text: string,
  kind: TranscriptKind,
  words: LiveWord[],
): { state: HeardState; newlyHeard: string[] } => {
  const present = wordsInText(text, words)
  const newlyHeard = present.filter((w) => state.heard.indexOf(w) === -1)
  return {
    state: {
      previous: kind === 'committed' ? [] : present,
      heard: newlyHeard.length ? state.heard.concat(newlyHeard) : state.heard,
    },
    newlyHeard,
  }
}

const FATAL_TYPES = [
  'auth_error',
  'quota_exceeded',
  'rate_limited',
  'resource_exhausted',
  'session_time_limit_exceeded',
  'unaccepted_terms',
  'input_error',
  'transcriber_error',
  'chunk_size_exceeded',
  'queue_overflow',
]

/** Reads a server message defensively: snake_case or camelCase keys, `text` or `transcript`. */
export const parseTranscriptMessage = (raw: unknown): ParsedMessage | null => {
  let msg: any = raw
  if (typeof raw === 'string') {
    try {
      msg = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!msg || typeof msg !== 'object') return null
  const type = String(msg.message_type ?? msg.messageType ?? msg.type ?? '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
  const text = typeof msg.text === 'string' ? msg.text : typeof msg.transcript === 'string' ? msg.transcript : ''
  if (type === 'partial_transcript' || type === 'partial') return { kind: 'partial', text }
  if (
    type === 'committed_transcript' ||
    type === 'committed_transcript_with_timestamps' ||
    type === 'final_transcript' ||
    type === 'committed'
  ) {
    return { kind: 'committed', text }
  }
  if (type === 'error' || type.endsWith('_error') || FATAL_TYPES.indexOf(type) !== -1) return { kind: 'error', text }
  return { kind: 'other', text }
}

export interface TranscribeGrant {
  token: string
  expiresAt?: number
  wsUrl: string
  query?: Record<string, string | number | boolean>
  remainingSeconds?: number
}

/** wsUrl + the backend's query + token + one `keyterms` param per practice word (max 50, 20 chars each). */
export const buildRealtimeUrl = (grant: TranscribeGrant, keyterms: string[]): string => {
  const params: string[] = []
  const add = (k: string, v: string) => params.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
  Object.keys(grant.query || {}).forEach((k) => add(k, String((grant.query as any)[k])))
  add('token', grant.token)
  keyterms
    .filter((k) => !!k && k.length <= 20)
    .slice(0, 50)
    .forEach((k) => add('keyterms', k))
  const sep = grant.wsUrl.indexOf('?') === -1 ? '?' : '&'
  return `${grant.wsUrl}${sep}${params.join('&')}`
}

const toBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const step = 0x8000
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode.apply(null, Array.prototype.slice.call(bytes.subarray(i, i + step)))
  }
  return btoa(binary)
}

export interface LiveSession {
  /** Ends the session and returns the seconds of audio streamed (already reported for the daily cap). */
  stop: () => Promise<number>
}

interface StartOptions {
  stream: MediaStream
  words: LiveWord[]
  fetchToken: () => Promise<TranscribeGrant>
  reportUsage: (seconds: number) => Promise<unknown>
  onHeard: (word: string) => void
  /** Called at most once, when the session can't continue. The step falls back to tapping silently. */
  onFail: () => void
}

const SAMPLE_RATE = 16000
const MAX_QUEUED_CHUNKS = 60 // about 6 s of audio buffered while the socket opens

export const startLiveTranscription = async ({
  stream,
  words,
  fetchToken,
  reportUsage,
  onHeard,
  onFail,
}: StartOptions): Promise<LiveSession | null> => {
  const AudioContextCtor: any =
    typeof window !== 'undefined' && (window.AudioContext || (window as any).webkitAudioContext)
  if (!AudioContextCtor || typeof WebSocket === 'undefined' || typeof AudioWorkletNode === 'undefined') return null

  let stopped = false
  let failed = false
  let samplesSent = 0
  let state = createHeardState()
  let ctx: AudioContext | null = null
  let source: MediaStreamAudioSourceNode | null = null
  let node: AudioWorkletNode | null = null
  let sink: GainNode | null = null
  let ws: WebSocket | null = null
  const queue: string[] = []

  const teardown = () => {
    try {
      if (node) node.port.onmessage = null
      source?.disconnect()
      node?.disconnect()
      sink?.disconnect()
    } catch (e) {}
    if (ctx && ctx.state !== 'closed') ctx.close().catch(() => undefined)
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try {
        ws.close(1000)
      } catch (e) {}
    }
  }

  const fail = () => {
    if (failed || stopped) return
    failed = true
    teardown()
    onFail()
  }

  let grant: TranscribeGrant
  try {
    grant = await fetchToken()
    if (!grant?.token || !grant?.wsUrl) return null
  } catch {
    return null
  }
  if (stopped) return null

  try {
    ctx = new AudioContextCtor() as AudioContext
    await ctx.audioWorklet.addModule(`${process.env.PUBLIC_URL || ''}/pcm16-worklet.js`)
    source = ctx.createMediaStreamSource(stream)
    node = new AudioWorkletNode(ctx, 'pcm16-downsampler', {
      processorOptions: { targetRate: SAMPLE_RATE, chunkMs: 100 },
    })
    // Keep the node pulled by the graph without making any sound.
    sink = ctx.createGain()
    sink.gain.value = 0
    source.connect(node)
    node.connect(sink)
    sink.connect(ctx.destination)
  } catch {
    teardown()
    return null
  }

  const send = (payload: string) => {
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(payload)
    else if (queue.length < MAX_QUEUED_CHUNKS) queue.push(payload)
  }

  node.port.onmessage = (e: MessageEvent) => {
    if (stopped || failed) return
    const buffer: ArrayBuffer = e.data
    if (!buffer || !buffer.byteLength) return
    samplesSent += buffer.byteLength / 2
    send(
      JSON.stringify({
        message_type: 'input_audio_chunk',
        audio_base_64: toBase64(buffer),
        commit: false,
        sample_rate: SAMPLE_RATE,
      }),
    )
  }

  try {
    ws = new WebSocket(
      buildRealtimeUrl(
        grant,
        words.map((w) => w.word),
      ),
    )
  } catch {
    fail()
    return null
  }
  ws.onopen = () => {
    while (queue.length && ws && ws.readyState === WebSocket.OPEN) ws.send(queue.shift() as string)
  }
  ws.onmessage = (e: MessageEvent) => {
    const parsed = parseTranscriptMessage(e.data)
    if (!parsed) return
    if (parsed.kind === 'error') {
      fail()
      return
    }
    if (parsed.kind !== 'partial' && parsed.kind !== 'committed') return
    const next = reduceTranscript(state, parsed.text, parsed.kind, words)
    state = next.state
    next.newlyHeard.forEach((w) => onHeard(w))
  }
  ws.onerror = () => fail()
  ws.onclose = () => {
    if (!stopped) fail()
  }

  return {
    stop: async () => {
      if (stopped) return Math.round(samplesSent / SAMPLE_RATE)
      stopped = true
      teardown()
      const seconds = Math.round(samplesSent / SAMPLE_RATE)
      if (seconds > 0) {
        try {
          await reportUsage(seconds)
        } catch {
          /* the daily cap is a safety net; never block the learner on it */
        }
      }
      return seconds
    },
  }
}
